import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Plus, Search } from 'lucide-react';
import { TableContent } from './LayoutPage';
import { useArticles, useAuthors, useArticleTypes, useSetArticlesStatus, authorDisplayName } from '../hooks/useArticles';
import type { Article } from '../hooks/useArticles';
import { CmsStateBadge } from '../components/cms/CmsStateBadge';
import { CmsThumb } from '../components/cms/CmsThumb';
import { useT } from '../i18n/admin';
import { useFdCan } from '../hooks/useFdCan';
import { cmsState, dayOf, expiresHint, todayInBusinessClock, STATE_META, type CmsState } from '../utils/cmsState';

type Filter = 'all' | CmsState;
const FILTERS: Filter[] = ['all', 'draft', 'scheduled', 'published', 'expired'];
const PAGE_SIZE = 12;

/** Newest first: by Publishes day, then by id. */
const newestFirst = (a: Article, b: Article) =>
  (dayOf(b.publish_date) || '').localeCompare(dayOf(a.publish_date) || '') || (b.id ?? 0) - (a.id ?? 0);

export const CMS = () => {
  const { articles, isLoading } = useArticles();
  const { authors } = useAuthors();
  const { articleTypes } = useArticleTypes();
  const { fmt } = useT();
  const navigate = useNavigate();
  const setStatus = useSetArticlesStatus();
  const canCreate = useFdCan('cms', 'create');
  const canEdit = useFdCan('cms', 'edit');

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [typeId, setTypeId] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const today = todayInBusinessClock();
  const authorMap = useMemo(() => new Map(authors.map((a) => [a.id, authorDisplayName(a)])), [authors]);
  const typeMap = useMemo(() => new Map(articleTypes.map((t) => [t.id, t.name])), [articleTypes]);

  const rows = useMemo(
    () => [...articles].sort(newestFirst).map((article) => ({ article, state: cmsState(article, today) })),
    [articles, today],
  );
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: rows.length, draft: 0, scheduled: 0, published: 0, expired: 0 };
    rows.forEach((r) => { c[r.state] += 1; });
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(({ article, state }) => {
      if (filter !== 'all' && state !== filter) return false;
      if (typeId && String(article.article_type_id) !== typeId) return false;
      if (!q) return true;
      const hay = [article.title_en, article.title_es, authorMap.get(article.author_id)].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, typeId, query, authorMap]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const pageRows = visible.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const pageIds = pageRows.map((r) => r.article.id!).filter(Boolean);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => (allOnPage ? next.delete(id) : next.add(id)));
      return next;
    });

  const bulk = (status: 'draft' | 'published') =>
    setStatus.mutate({ ids: [...selected], status }, { onSuccess: () => setSelected(new Set()) });

  const date = (v?: string | null) => fmt.date(dayOf(v)) || '-';
  const filterLabel = (f: Filter) => (f === 'all' ? 'All' : STATE_META[f].label);

  return (
    <TableContent>
      <div className="flex h-full flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[13px] font-semibold text-gray-500">CMS</div>
            <h1 className="m-0 text-2xl font-bold text-gray-900">Articles</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">What residents read in the app · today is {fmt.date(today)}</p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => navigate('/settings/articles/new')}
              className="flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white"
              style={{ background: 'var(--tenant-primary, #111827)' }}
            >
              <Plus size={16} />
              Add article
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by state">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`rounded-full border px-3 py-1 text-[13px] font-semibold ${
                filter === f ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              {filterLabel(f)} <span className="opacity-70">{counts[f]}</span>
            </button>
          ))}
          <span className="flex-1" />
          <label className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-500">
            <Search size={14} aria-hidden />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search title or author"
              aria-label="Search title or author"
              className="w-56 border-0 bg-transparent text-sm text-gray-900 outline-none"
            />
          </label>
          <select
            value={typeId}
            onChange={(e) => { setTypeId(e.target.value); setPage(1); }}
            aria-label="Filter by type"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
          >
            <option value="">All types</option>
            {articleTypes.map((t) => (
              <option key={t.id} value={String(t.id)}>{t.name}</option>
            ))}
          </select>
        </div>

        {canEdit && selected.size > 0 && (
          <div className="flex items-center gap-3 rounded-lg bg-gray-100 px-4 py-2 text-sm">
            <span className="font-semibold">{selected.size} selected</span>
            <span className="flex-1" />
            <button type="button" disabled={setStatus.isPending} onClick={() => bulk('draft')} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 disabled:opacity-50">
              Move to draft
            </button>
            <button type="button" disabled={setStatus.isPending} onClick={() => bulk('published')} className="rounded-lg px-3 py-1.5 font-semibold text-white disabled:opacity-50" style={{ background: 'var(--tenant-primary, #111827)' }}>
              Publish
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto rounded-lg bg-white shadow-sm">
          {!isLoading && articles.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="m-0 font-medium">No articles found</p>
              {canCreate && <p className="m-0 mt-2 text-sm">Click "Add article" to create the first one</p>}
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="w-10 px-4 py-3">
                    {canEdit && <input type="checkbox" aria-label="Select all on this page" checked={allOnPage} onChange={toggleAll} />}
                  </th>
                  <th className="px-2 py-3">Title</th>
                  <th className="px-2 py-3">Type</th>
                  <th className="px-2 py-3">Author</th>
                  <th className="px-2 py-3">Publishes</th>
                  <th className="px-2 py-3">Expires</th>
                  <th className="px-2 py-3">Status</th>
                  <th className="w-10 px-2 py-3"><span className="sr-only">Preview</span></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(({ article, state }) => {
                  const hint = expiresHint(article, today);
                  return (
                    <tr
                      key={article.id}
                      onClick={() => navigate(`/settings/articles/${article.id}`)}
                      className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${state === 'expired' ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <input
                            type="checkbox"
                            aria-label={`Select ${article.title_en}`}
                            checked={selected.has(article.id!)}
                            onChange={() => toggle(article.id!)}
                          />
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-3">
                          <CmsThumb imageKey={article.art_img_url} width={64} height={40} />
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{article.title_en || '-'}</div>
                            <div className="text-[13px] text-gray-500">{article.title_es}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3">{typeMap.get(article.article_type_id) || '-'}</td>
                      <td className="px-2 py-3">{authorMap.get(article.author_id) || '-'}</td>
                      <td className="whitespace-nowrap px-2 py-3">{date(article.publish_date)}</td>
                      <td className="whitespace-nowrap px-2 py-3">
                        {date(article.expiration_date)}
                        {hint && <div className="text-xs text-gray-500">{hint}</div>}
                      </td>
                      <td className="px-2 py-3"><CmsStateBadge state={state} /></td>
                      <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          aria-label={`Preview ${article.title_en}`}
                          title="Preview in app"
                          onClick={() => navigate(`/settings/articles/${article.id}/preview`)}
                          className="border-0 bg-transparent p-1 text-gray-500 hover:text-gray-900"
                        >
                          <Eye size={16} aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between text-[13px] text-gray-500">
          <span>Showing {pageRows.length} of {visible.length} · newest first</span>
          <span className="flex items-center gap-2">
            <button type="button" disabled={current <= 1} onClick={() => setPage(current - 1)} className="rounded border border-gray-300 bg-white px-2 py-1 disabled:opacity-40">Previous</button>
            <span>{current} / {pageCount}</span>
            <button type="button" disabled={current >= pageCount} onClick={() => setPage(current + 1)} className="rounded border border-gray-300 bg-white px-2 py-1 disabled:opacity-40">Next</button>
          </span>
        </div>

      </div>
    </TableContent>
  );
};
